<!--
  Zahlungsmethode als Pop-up erfassen — für Upgrade, Nachbuchen und Modul
  buchen. Als Modal statt inline am Kartenende: Sonst öffnete sich das
  Formular unbemerkt außerhalb des sichtbaren Bereichs, und der Kunde musste
  erst manuell herunterscrollen, um zu sehen, dass sich überhaupt etwas
  getan hat.

  Kein Rauswurf zu einer externen Checkout-Seite: Ein SetupIntent plus
  Stripe-Elements-Formular bleiben im Portal. Kartendaten laufen dabei nie
  über unseren Server, nur über Stripe.js direkt zu Stripe (Konzept 9.6).
-->
<script lang="ts">
  import { loadStripe, type Stripe, type StripeElements } from '@stripe/stripe-js';
  import { api } from './api.ts';

  let { onErfasst, onAbbrechen }: { onErfasst: () => void; onAbbrechen: () => void } = $props();

  // Hintergrund darf nicht mitscrollen, solange das Pop-up offen ist.
  $effect(() => {
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = vorher;
    };
  });

  function beiTastendruck(ereignis: KeyboardEvent): void {
    if (ereignis.key === 'Escape') onAbbrechen();
  }

  let formularElement: HTMLDivElement | undefined = $state();
  let bereit = $state(false);
  let sendet = $state(false);
  let fehler = $state<string | null>(null);

  let stripeClient: Stripe | null = null;
  let elements: StripeElements | null = null;

  $effect(() => {
    let abgebrochen = false;
    void (async () => {
      try {
        const schluessel = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
        if (!schluessel) {
          fehler = 'Zahlungsmethoden lassen sich hier gerade nicht erfassen.';
          return;
        }
        const [client, intent] = await Promise.all([
          loadStripe(schluessel),
          api.post<{ client_secret: string }>('/api/portal/zahlungsmethode/setup-intent'),
        ]);
        if (abgebrochen || !client) return;
        stripeClient = client;
        elements = client.elements({ clientSecret: intent.client_secret });
        const zahlungselement = elements.create('payment');
        if (formularElement) zahlungselement.mount(formularElement);
        bereit = true;
      } catch (f) {
        fehler = f instanceof Error ? f.message : 'Das Formular ließ sich nicht laden';
      }
    })();
    return () => {
      abgebrochen = true;
    };
  });

  async function absenden(ereignis: SubmitEvent): Promise<void> {
    ereignis.preventDefault();
    if (!stripeClient || !elements) return;
    sendet = true;
    fehler = null;
    try {
      const { error, setupIntent } = await stripeClient.confirmSetup({
        elements,
        redirect: 'if_required',
      });
      if (error || !setupIntent) {
        fehler = error?.message ?? 'Die Zahlungsmethode konnte nicht bestätigt werden';
        return;
      }
      const zahlungsmethode =
        typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent.payment_method?.id;
      if (!zahlungsmethode) {
        fehler = 'Die Zahlungsmethode konnte nicht bestätigt werden';
        return;
      }
      await api.post('/api/portal/zahlungsmethode/bestaetigen', {
        payment_method_id: zahlungsmethode,
      });
      onErfasst();
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Das hat gerade nicht geklappt';
    } finally {
      sendet = false;
    }
  }
</script>

<svelte:window onkeydown={beiTastendruck} />

<!-- Rein dekorativer Hintergrund: die eigentliche Schließen-Bedienung ist
     tastaturzugänglich über Esc (siehe oben) und den „Abbrechen“-Knopf. -->
<div class="zahlungsmethode-hintergrund" role="presentation" onclick={onAbbrechen}>
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div
    class="karte zahlungsmethode-fenster"
    role="dialog"
    aria-modal="true"
    aria-labelledby="zm-titel"
    tabindex="-1"
    onclick={(e) => e.stopPropagation()}
  >
    <h3 id="zm-titel">Zahlungsmethode hinterlegen</h3>
    <p class="leise klein">Erst danach lässt sich diese Aktion abschließen.</p>
    {#if fehler}<p class="fehler">{fehler}</p>{/if}
    <form onsubmit={absenden}>
      <div bind:this={formularElement}></div>
      <div class="knopfreihe">
        <button type="submit" disabled={!bereit || sendet}>
          {sendet ? 'Wird bestätigt …' : 'Zahlungsmethode speichern'}
        </button>
        <button type="button" class="sekundaer" onclick={onAbbrechen}>Abbrechen</button>
      </div>
    </form>
  </div>
</div>

<style>
  .zahlungsmethode-hintergrund {
    position: fixed;
    inset: 0;
    background: rgba(18, 42, 66, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    z-index: 1000;
  }

  .zahlungsmethode-fenster {
    width: 100%;
    max-width: 28rem;
    max-height: 90vh;
    overflow-y: auto;
    margin: 0;
    cursor: auto;
  }
</style>
